import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getRecaptchaToken, loadRecaptcha } from '../../utils/recaptcha';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha().catch((err) => {
        console.warn('reCAPTCHA pre-load failed:', err);
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const recaptchaToken = await getRecaptchaToken('password_reset');

      if (!recaptchaToken && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        setError('Kunne ikke verifisere. Vennligst prøv igjen.');
        return;
      }

      const submitData = {
        email,
        recaptchaToken,
        website: '',
        email_confirm: '',
        _submission_time: Date.now().toString(),
      };
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        submitData,
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setMessage('Instruksjoner for å nullstille passord er sendt til din e-post');
        setError('');
        setShowResendVerification(false);
      } else {
        const errorMsg = data.message || 'Kunne ikke behandle forespørselen';
        setError(errorMsg);
        setMessage('');
        setShowResendVerification(
          errorMsg.includes('ikke verifisert') || errorMsg.includes('verifiser e-postadressen')
        );
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Kunne ikke behandle forespørselen';
      setError(errorMsg);
      setMessage('');
      setShowResendVerification(
        errorMsg.includes('ikke verifisert') || errorMsg.includes('verifiser e-postadressen')
      );
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Vennligst skriv inn e-postadressen din først');
      return;
    }

    setResendingVerification(true);
    setResendMessage('');
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`,
        { email }
      );

      if (response.data.success) {
        setResendMessage('Verifiserings-e-post er sendt. Sjekk innboksen din.');
        setShowResendVerification(false);
      } else {
        setError(response.data.message || 'Kunne ikke sende verifiserings-e-post');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunne ikke sende verifiserings-e-post');
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="login-page forgot-password-page">
      <div className="login-page-container">
        <header className="login-page-brand">
          <Link to="/">Lopeglad</Link>
          <p>Tilbakestill passordet ditt</p>
        </header>

        <div className="login-page-card">
          <form onSubmit={handleSubmit} className="login-page-form">
            <h2>Glemt passord</h2>

            {message && <div className="login-page-alert login-page-alert--success">{message}</div>}
            {resendMessage && <div className="login-page-alert login-page-alert--success">{resendMessage}</div>}
            {error && <div className="login-page-alert login-page-alert--error">{error}</div>}

            {showResendVerification && (
              <div className="forgot-password-verify">
                <p>E-postadressen din er ikke verifisert. Vennligst verifiser e-postadressen først.</p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                >
                  {resendingVerification ? 'Sender…' : 'Send verifiserings-e-post på nytt'}
                </button>
              </div>
            )}

            <div className="login-page-field">
              <label htmlFor="forgot-email">E-post</label>
              <input
                id="forgot-email"
                type="email"
                placeholder="din@epost.no"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <input type="text" name="website" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" value="" readOnly />
            <input type="text" name="email_confirm" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" value="" readOnly />

            <button type="submit" className="login-page-submit">
              Nullstill passord
            </button>

            <Link to="/login" className="forgot-password-back">
              Tilbake til innlogging
            </Link>

            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <div className="login-page-recaptcha">
                <small>
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  {' '}and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}apply.
                </small>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
